import { ActiveEnvironmentsLineChart } from "@/components/insights/ActiveEnvironmentsLineChart";
import { ActiveUsersLineChart } from "@/components/insights/ActiveUsersLineChart";
import { EnvironmentRuntimeLineChart } from "@/components/insights/EnvironmentRuntimeLineChart";
import { InsightCards } from "@/components/insights/InsightsCardRibbon";
import { InsightsTopTablesList } from "@/components/insights/InsightsTopTablesList";
import { NotAuthorized } from "@/components/NotAuthorized";
import { LoadingState } from "@/components/podkit/loading/LoadingState";
import { Tabs, TabsContent } from "@/components/podkit/tabs/Tabs";
import { ExternalLink } from "@/components/podkit/typography/ExternalLink";
import { Heading1 } from "@/components/podkit/typography/Headings";
import { ProjectCombobox } from "@/components/projects/ProjectCombobox";
import { TimeRangeSelector } from "@/components/TimeRangeSelector";
import { Flags } from "@/feature-flags";
import { useDocumentTitle } from "@/hooks/use-document-title";
import { useFlag } from "@/hooks/use-flag";
import { useMembership } from "@/hooks/use-membership";
import { useOrganization } from "@/queries/organization-queries";
import { useProject } from "@/queries/project-queries";
import { parseAsTimeRange, parseAsView, TIME_RANGES, type View } from "@/routes/insights/insights";
import { InsightsEducationPage } from "@/routes/insights/InsightsEducationPage"; // Import the new education page
import { Timestamp } from "@bufbuild/protobuf";
import { OrganizationRole, OrganizationTier } from "gitpod-next-api/gitpod/v1/organization_pb";
import { ChartNoAxesCombined } from "lucide-react";
import { parseAsString, useQueryState } from "nuqs";
import { useEffect, useMemo, useState, type FC } from "react";
import { useLocation, useNavigate } from "react-router-dom";

export const InsightsPage: FC = () => {
    useDocumentTitle("Insights");

    const { data: organization, isPending: isPendingOrganization } = useOrganization();
    const { membership, isPending: isPendingMembership } = useMembership();

    if (isPendingOrganization || isPendingMembership) {
        return <LoadingState />;
    }

    // Check if user is an admin
    const isOrgAdmin = membership?.userRole === OrganizationRole.ADMIN;
    if (!isOrgAdmin) {
        return (
            <NotAuthorized
                heading="You're not authorized"
                description="You do not have sufficient permissions to view insights. Only organization admins can access this page."
            />
        );
    }

    const isEnterpriseTier = organization?.tier === OrganizationTier.ENTERPRISE;
    if (!isEnterpriseTier) {
        return <InsightsEducationPage />;
    }

    return <InsightsPageContent />;
};

export const InsightsPageContent: FC = () => {
    const navigate = useNavigate();

    const { value: isInsightsDisabled } = useFlag(Flags.InsightsDisabled, false);

    useEffect(() => {
        if (isInsightsDisabled) {
            navigate("/");
            return;
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps -- putting setTierViolation in the dependency array would cause an infinite loop
    }, [navigate]);

    const [selectedRange, setSelectedRangeState] = useQueryState(
        "selectedRange",
        parseAsTimeRange.withDefault(TIME_RANGES[1]),
    ); // Default to "Last Week"

    const [selectedView, setSelectedView] = useQueryState<View>(
        "view",
        parseAsView.withDefault("activeUsers").withOptions({ clearOnDefault: false }),
    );
    const timeRanges = useMemo(() => {
        return TIME_RANGES.map((range) => ({
            ...range,
            disabled: selectedView === "activeUsers" && range.value === "last-day",
        }));
    }, [selectedView]);
    // this effect is correcting the selected range when the view changes by selecting the first non-disabled range
    useEffect(() => {
        const idx = timeRanges.findIndex((r) => r.value === selectedRange.value);
        const closestMatch = timeRanges.find((r, i) => !r.disabled && i >= idx);
        if (closestMatch) {
            void setSelectedRangeState(closestMatch);
        }
    }, [selectedView, selectedRange, timeRanges, setSelectedRangeState]);

    const startTime = useMemo(
        () => new Date(new Date().setDate(new Date().getDate() - selectedRange.days)),
        [selectedRange],
    );
    const endTime = useMemo(() => new Date(), []);
    const startTimestamp = useMemo(() => Timestamp.fromDate(startTime), [startTime]);
    const endTimestamp = useMemo(() => Timestamp.fromDate(endTime), [endTime]);

    const [selectedProject, setSelectedProject] = useQueryState("project", parseAsString);

    const { data: selectedProjectData } = useProject(selectedProject ?? undefined);

    const location = useLocation();
    const [highlightMostActiveUsers, setHighlightMostActiveUsers] = useState(false);
    useEffect(() => {
        if (location.hash === "#most-active-users") {
            setHighlightMostActiveUsers(true);
            const timeout = setTimeout(() => setHighlightMostActiveUsers(false), 3000);
            return () => clearTimeout(timeout);
        }
    }, [location.hash]);

    return (
        <div data-testid="insights-page" className="flex flex-col gap-6">
            <div data-testid="insights-page-contents" className="contents">
                <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                    <div className="flex flex-col gap-1">
                        <Heading1>Insights</Heading1>
                        <div className="flex items-center gap-2 text-sm">
                            <ChartNoAxesCombined size={16} aria-hidden className="text-content-strong" />
                            <span className="text-content-strong">
                                Need more data? Check out the{" "}
                                <ExternalLink
                                    className="text-sm text-content-brand underline"
                                    iconSize="sm"
                                    iconClassName="size-3.5"
                                    href="https://ona.com/docs/api-reference/resources/usage/methods/list_environment_runtime_records/"
                                >
                                    Insights API
                                </ExternalLink>
                            </span>
                        </div>
                    </div>
                    <div className="flex w-full items-center gap-2 text-sm lg:w-auto">
                        <label htmlFor="time-range-selector" className="sr-only">
                            Select time range
                        </label>
                        <div className="flex-1 lg:flex-none">
                            <TimeRangeSelector
                                timeRanges={timeRanges}
                                selectedRange={selectedRange}
                                setSelectedRange={setSelectedRangeState}
                            />
                        </div>
                        <span aria-hidden="true">for</span>
                        <label htmlFor="project-selector" className="sr-only">
                            Select project
                        </label>
                        <div className="flex-1 lg:w-72 lg:flex-none">
                            <ProjectCombobox
                                selectedProject={selectedProject}
                                onProjectChange={(value) => setSelectedProject(value === "all" ? null : value)}
                                className="w-full bg-surface-pure"
                            />
                        </div>
                    </div>
                </div>

                {/* Tabs for switching views */}
                <Tabs
                    value={selectedView}
                    onValueChange={(value) => setSelectedView(value as View)}
                    className="flex w-full flex-col gap-6"
                    aria-label="Insights data visualization"
                >
                    <InsightCards
                        startTimestamp={startTimestamp}
                        endTimestamp={endTimestamp}
                        selectedRange={selectedRange}
                        projectId={selectedProject ?? undefined}
                    />

                    <div className="w-full rounded-xl bg-surface-01">
                        <TabsContent value="activeUsers">
                            <ActiveUsersLineChart
                                startTimestamp={startTimestamp}
                                endTimestamp={endTimestamp}
                                selectedRange={selectedRange}
                                projectId={selectedProject ?? undefined}
                            />
                        </TabsContent>
                        <TabsContent value="activeEnvironments">
                            <ActiveEnvironmentsLineChart
                                startTimestamp={startTimestamp}
                                endTimestamp={endTimestamp}
                                selectedRange={selectedRange}
                                projectId={selectedProject ?? undefined}
                            />
                        </TabsContent>
                        <TabsContent value="environmentRuntime">
                            <EnvironmentRuntimeLineChart
                                startTimestamp={startTimestamp}
                                endTimestamp={endTimestamp}
                                selectedRange={selectedRange}
                                projectId={selectedProject ?? undefined}
                            />
                        </TabsContent>
                    </div>
                </Tabs>

                <InsightsTopTablesList
                    startTimestamp={startTimestamp}
                    endTimestamp={endTimestamp}
                    selectedRange={selectedRange}
                    projectId={selectedProject ?? undefined}
                    projectName={selectedProjectData?.metadata?.name}
                    highlightMostActiveUsers={highlightMostActiveUsers}
                />
            </div>
        </div>
    );
};
